import { Trans } from "@lingui/react/macro";
import { useId, useState } from "react";

import { useCurrentUser } from "@/features/auth";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";

import { useFriends } from "../../api/useFriends";
import { useRefreshFriends } from "../../api/useRefreshFriends";
import { useUpdateFriendsVisible } from "../../api/useUpdateFriendsVisible";
import { awards } from "../../model/awards";
import { DEFAULT_FRIENDS_METRIC } from "../../model/FRIENDS_METRICS";
import { DEFAULT_FRIENDS_PERIOD } from "../../model/FRIENDS_PERIODS";
import type { FriendsSearch } from "../../model/friendsSearchSchema";
import { placeMoves } from "../../model/rankPlayers";
import { AwardsSection } from "./AwardsSection";
import { FriendsHeader } from "./FriendsHeader";
import { FriendsTable } from "./FriendsTable";
import { HiddenNotice } from "./HiddenNotice";
import { InvitesSection } from "./InvitesSection";
import { MatesSection } from "./MatesSection";
import { PlaceCard } from "./PlaceCard";
import { PrivateListNotice } from "./PrivateListNotice";
import { VisibilitySection } from "./VisibilitySection";

type FriendsPageProps = {
  search: FriendsSearch;
  onSearchChange: (search: FriendsSearch) => void;
};

// Экран «Друзья» по макету design/prototype/FriendsRelease.dc.html: твоё место, таблица
// друзей в Relock со сравнением один на один, напарники, номинации, кого позвать и показ
// себя друзьям. Данные — `GET /api/friends` за период; период и показатель таблицы — в
// search params маршрута.
export const FriendsPage = ({ search, onSearchChange }: FriendsPageProps) => {
  const period = search.period ?? DEFAULT_FRIENDS_PERIOD;
  const metric = search.metric ?? DEFAULT_FRIENDS_METRIC;
  const invitesAnchor = useId();
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null);
  const friends = useFriends(period, refreshedAt);
  const refresh = useRefreshFriends();
  const updateVisible = useUpdateFriendsVisible();
  const { data: user } = useCurrentUser();
  const board = friends.data?.board ?? null;
  const checking =
    refresh.isPending ||
    (refreshedAt !== null &&
      friends.isFetching &&
      (friends.data?.synced_at == null || Date.parse(friends.data.synced_at) < refreshedAt));

  // Смена периода или фоновое перечитывание: прошлая таблица на экране, над ней — полоса.
  const refreshing = friends.isFetching && !friends.isPending && !checking;

  const onRefresh = () => {
    refresh.mutate(
      {},
      {
        onSuccess: () => {
          setRefreshedAt(Date.now());
        },
      },
    );
  };

  const content = () => {
    if (friends.isPending || friends.data?.status === "pending") {
      return (
        <div data-state="loading" className="flex flex-col gap-5">
          <p className="text-sm text-muted">
            <Trans>Loading your friends from Steam…</Trans>
          </p>
          <Skeleton className="h-28 rounded-xl" />
          <Skeleton className="h-96 rounded-xl" />
        </div>
      );
    }
    if (friends.isError) {
      return (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface p-5">
          <p className="text-foreground-soft">
            <Trans>Could not load your friends.</Trans>
          </p>
          <Button variant="outline" onClick={() => void friends.refetch()}>
            <Trans>Try again</Trans>
          </Button>
        </div>
      );
    }
    if (board === null) {
      return (
        <p className="text-muted">
          <Trans>Friends are only available for Steam accounts.</Trans>
        </p>
      );
    }
    const players = [board.me, ...board.friends];
    const showTable = !board.list_private && !board.hidden;
    const periodAwards = showTable ? awards(players, period) : [];
    return (
      <>
        {board.list_private && <PrivateListNotice onRefresh={onRefresh} checking={checking} />}
        {board.hidden && <HiddenNotice />}
        {showTable && board.friends.length > 0 && (
          <PlaceCard
            players={players}
            meId={board.me.account_id}
            metric={metric}
            move={placeMoves(players, metric).get(board.me.account_id) ?? null}
          />
        )}
        {showTable && (
          <FriendsTable
            me={board.me}
            friends={board.friends}
            metric={metric}
            onMetricChange={(next) => {
              onSearchChange({ ...search, metric: next });
            }}
            invitesAnchor={board.invites.length > 0 ? invitesAnchor : null}
            refreshing={refreshing}
          />
        )}
        <MatesSection
          board={board}
          period={period}
          minMatches={board.min_mate_matches}
          refreshing={refreshing}
        />
        {periodAwards.length > 0 && (
          <AwardsSection awards={periodAwards} period={period} meId={board.me.account_id} />
        )}
        {!board.list_private && <InvitesSection invites={board.invites} anchorId={invitesAnchor} />}
        {user !== undefined && (
          <VisibilitySection
            visible={user.friends_visible}
            pending={updateVisible.isPending}
            onChange={(visible) => {
              updateVisible.mutate({ body: { friends_visible: visible } });
            }}
          />
        )}
      </>
    );
  };

  return (
    <main
      data-page="friends"
      className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-6 pt-7 pb-16"
    >
      <FriendsHeader
        period={period}
        onPeriodChange={(next) => {
          onSearchChange({ ...search, period: next });
        }}
      />
      {content()}
    </main>
  );
};
